import {
  AttributeDefinition,
  AttributeDefinitionValue,
  Cat,
  MultiSelectOption,
} from '@dispatch/shared/types';
import { SelectField } from '@dispatch/web/ui';
import { Chip, Grid } from '@mui/material';
import React from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form';

export const ClaimAttributesSection = ({
  control,
  errors,
  attributes,
  setValue,
}: {
  setValue?: UseFormSetValue<FieldValues>;
  errors: FieldErrors;
  control: Control<FieldValues>;
  hasUpsertPermissions: unknown;
  attributes?:
    | {
        definition: AttributeDefinitionValue | undefined;
        definitionParent: AttributeDefinition | undefined;
        id: string;
        cat: Cat;
        nonCat: Cat;
        useProficiency: boolean;
        minimumProficiency: number;
        isPrepopulated: boolean;
      }[]
    | undefined;
}) => {
  // Normalize attributes to always be an array (never undefined/null)
  const safeAttributes = React.useMemo(
    () => (Array.isArray(attributes) ? attributes : []),
    [attributes]
  );
  const attributeOptions: MultiSelectOption[] = React.useMemo(() => {
    try {
      if (safeAttributes.length === 0) return [];

      return safeAttributes
        .filter((a) => a != null && (a?.definition?.integrationGUID || a?.id)) // Filter out invalid attributes
        .map((a) => ({
          value: a?.definition?.integrationGUID || a?.id || '',
          title: a?.definition?.displayName || a?.id || 'Unknown',
          obj: a?.definitionParent,
        }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error building attributeOptions:', error);
      return [];
    }
  }, [safeAttributes]);

  React.useEffect(() => {
    try {
      setValue?.(
        'attributes',
        attributeOptions?.map((a) => a?.value).filter((v) => v != null)
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error setting attribute values:', error);
    }
  }, [attributeOptions, setValue]);

  // renders tags for attributes, and allows to show the display name of the attribute
  // this is for attributes that have a value of 'Active', to show more context
  const renderTags = React.useCallback(
    (value, getTagProps) => {
      try {
        if (!Array.isArray(value)) {
          // eslint-disable-next-line no-console
          console.warn('renderTags received non-array value:', value);
          return [];
        }

        return value
          .filter((option) => option != null)
          .map((option: MultiSelectOption, index: number) => {
            try {
              const foundOption = attributeOptions?.find(
                (opt) => String(opt?.value) === String(option)
              ) as
                | (MultiSelectOption & { obj?: AttributeDefinition })
                | undefined;

              const title = foundOption?.title ?? String(option);
              const obj = foundOption?.obj;

              // Generate a safe key - prefer option.value, fallback to option itself, then index
              const key =
                typeof option === 'object' && option?.value
                  ? option.value
                  : option ?? `attr-${index}`;

              return (
                <Chip
                  {...getTagProps({ index })}
                  key={key}
                  label={
                    String(title).toLowerCase() === 'active'
                      ? `${obj?.displayName || 'Unknown'} - ${title}`
                      : title
                  }
                  sx={{ m: 0.5 }}
                />
              );
            } catch (chipError) {
              // eslint-disable-next-line no-console
              console.error('Error rendering individual chip:', {
                option,
                index,
                error: chipError,
              });
              // Return a fallback chip for this one bad option
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={`error-${index}`}
                  label="Unknown Attribute"
                  sx={{ m: 0.5 }}
                />
              );
            }
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in renderTags:', {
          error,
          value,
          attributeOptions,
        });
        // Return empty array to prevent crash
        return [];
      }
    },
    [attributeOptions]
  );

  return (
    <Grid container>
      <Grid container spacing={2} marginBottom={2} data-testid="attributes">
        <Grid item xs={12}>
          <SelectField
            multiple
            id="attributes"
            name="attributes"
            label="Attributes"
            required
            control={control}
            errors={errors}
            readOnly
            options={attributeOptions || []}
            disabled
            renderTags={renderTags}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
